import { useQueryClient } from "@tanstack/react-query";
import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/shared/components/DeleteConfirmationDialog";
import type { TanStackColumn } from "@/shared/components/TanStackTable";
import { Button } from "@/shared/ui/Button";
import { Checkbox } from "@/shared/ui/Checkbox";
import { extractApiErrorDetail } from "@/shared/utils/errorHandling";
import { ACCOUNTING_QUERY_KEYS } from "../../configs/queryKeys";
import { invalidateOrgOperationTypeCaches } from "../../hooks/useAccountingQueries";
import { accountingService } from "../../services/accounting.service";
import type { LedgerAllocationOperationType } from "../../types/accounting.types";

type CategoryBulkActionsOptions = {
  /**
   * Appelé pour chaque catégorie désactivée ou supprimée avec succès. Le
   * drawer d'affectation s'en sert pour oublier la catégorie du brouillon en
   * cours, comme après une suppression unitaire : sinon l'enregistrement la
   * recréerait sous le même nom.
   */
  onRemoved?: (operationTypeId: string) => void
};

/** Selection is limited to the visible categories of the current budget. */
export const useCategoryBulkActions = (
  rows: LedgerAllocationOperationType[],
  scope: string,
  { onRemoved }: CategoryBulkActionsOptions = {},
) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const queryClient = useQueryClient();
  useEffect(() => {
    setSelectedIds(new Set());
    setConfirmDelete(false);
  }, [scope]);

  const selected = rows.filter((row) => selectedIds.has(row.id));
  const run = async (action: "disable" | "delete") => {
    if (running.current || selected.length === 0) return;
    running.current = true;
    setBusy(true);
    setConfirmDelete(false);
    let succeeded = 0;
    let failed = 0;
    let lastError: unknown = null;
    // Appels séquentiels au service, pas aux mutations : chaque mutation
    // invaliderait quatre listes (catégories, affectations, budgets, synthèse)
    // à chaque catégorie. On invalide une seule fois après la boucle. Les
    // catégories en échec restent sélectionnées pour une nouvelle tentative.
    for (const category of selected) {
      try {
        if (action === "delete") await accountingService.deleteOrgOperationType(category.id);
        else await accountingService.updateOrgOperationType({ id: category.id, actif: false });
        succeeded += 1;
        onRemoved?.(category.id);
        setSelectedIds((previous) => {
          const next = new Set(previous);
          next.delete(category.id);
          return next;
        });
      }
      catch (error) {
        failed += 1;
        lastError = error;
      }
    }
    if (succeeded > 0) {
      invalidateOrgOperationTypeCaches(queryClient);
      if (action === "delete") void queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      toast.success(`${succeeded} catégorie(s) ${action === "delete" ? "supprimée(s)" : "désactivée(s)"}`);
    }
    if (failed > 0) {
      const detail = extractApiErrorDetail(lastError);
      toast.error(`${failed} catégorie(s) en échec, toujours sélectionnée(s)`, detail ? { description: detail } : undefined);
    }
    running.current = false;
    setBusy(false);
  };

  const selectionColumn = useMemo((): TanStackColumn<LedgerAllocationOperationType> => ({
    // « select » : identifiant que TanStackTable épingle en première position
    // et garde visible, comme les autres colonnes de sélection du dépôt.
    id: "select",
    label: "Sélection",
    isSelectionColumn: true,
    enableSorting: false,
    size: 60,
    // La case d'en-tête ne porte que sur les lignes du tableau qui la rend :
    // la page Budgets partage cette colonne entre le tableau des dépenses et
    // celui des recettes, cocher l'un ne doit pas embarquer l'autre.
    header: ({ table }) => {
      const tableIds = table.getRowModel().rows.map((row) => row.original.id);
      const selectedInTable = tableIds.filter((id) => selectedIds.has(id)).length;
      const state = selectedInTable === 0 ? false : selectedInTable === tableIds.length ? true : "indeterminate";
      return (
        <Checkbox
          aria-label="Sélectionner toutes les catégories affichées"
          checked={state}
          indeterminate={state === "indeterminate"}
          disabled={busy || tableIds.length === 0}
          onCheckedChange={(checked) => setSelectedIds((previous) => {
            const next = new Set(previous);
            tableIds.forEach((id) => (checked === true ? next.add(id) : next.delete(id)));
            return next;
          })}
        />
      );
    },
    cell: ({ row }) => (
      <Checkbox
        aria-label={`Sélectionner ${row.original.name}`}
        checked={selectedIds.has(row.original.id)}
        disabled={busy}
        onCheckedChange={(checked) => setSelectedIds((previous) => {
          const next = new Set(previous);
          if (checked === true) next.add(row.original.id);
          else next.delete(row.original.id);
          return next;
        })}
      />
    ),
  }), [selectedIds, busy]);

  const allocationCount = selected.reduce((sum, category) => sum + (category.ledger_allocations_count ?? 0), 0);
  const toolbar = (
    <>
      {(selected.length > 0 || busy) && (
        <div className="flex flex-wrap items-center gap-2" role="region" aria-label="Actions sur les catégories sélectionnées">
          <span className="text-xs">
            {busy ? "Traitement en cours..." : `${selected.length} catégorie(s) sélectionnée(s)`}
          </span>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => void run("disable")}>Désactiver la sélection</Button>
          <Button size="sm" variant="destructive" disabled={busy} onClick={() => setConfirmDelete(true)}>Supprimer la sélection</Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setSelectedIds(new Set())}>Annuler la sélection</Button>
        </div>
      )}
      <DeleteConfirmationDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => void run("delete")}
        isLoading={busy}
        itemName={`${selected.length} catégories`}
        title={`Supprimer ${selected.length} catégorie(s) ?`}
        description={`${allocationCount} affectation(s) seront conservées mais décatégorisées. Les budgets et règles de catégorisation associés seront supprimés. Cette action est irréversible.`}
        confirmLabel="Supprimer les catégories sélectionnées"
      />
    </>
  );
  return { selectionColumn, toolbar, busy };
};
